/**
 * Install pulse_diag browser probes beside existing clientRca (instrumentation only).
 */
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type TrackPublication,
} from 'livekit-client';
import { BrowserDiagCollector, resolveBrowserActivation } from './v1/collector';

function findPublisherPc(room: Room): RTCPeerConnection | null {
  const engine = (room as unknown as { engine?: Record<string, unknown> }).engine;
  if (!engine) return null;
  const pcManager = engine.pcManager as { publisher?: { pc?: RTCPeerConnection } } | undefined;
  if (pcManager?.publisher?.pc) return pcManager.publisher.pc;
  const publisher = engine.publisher as { pc?: RTCPeerConnection } | undefined;
  return publisher?.pc ?? null;
}

async function collectPerStreamStats(room: Room): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {
    connection_quality: String(room.localParticipant.connectionQuality ?? ''),
    streams: [] as Record<string, unknown>[],
  };
  const pc = findPublisherPc(room);
  if (!pc || typeof pc.getStats !== 'function') {
    return {
      ...out,
      unavailable: { getStats: 'publisher_pc_unavailable' },
    };
  }
  const report = await pc.getStats();
  const remoteInbound = new Map<number, RTCStats>();
  const mediaSources = new Map<string, RTCStats>();
  report.forEach((s) => {
    if (s.type === 'remote-inbound-rtp') {
      remoteInbound.set(Number((s as { ssrc?: number }).ssrc || 0), s);
    }
    if (s.type === 'media-source') {
      mediaSources.set(s.id, s);
    }
  });
  const streams: Record<string, unknown>[] = [];
  report.forEach((s) => {
    if (s.type !== 'outbound-rtp') return;
    const o = s as RTCOutboundRtpStreamStats & {
      kind?: string;
      mediaType?: string;
      nackCount?: number;
      mediaSourceId?: string;
      remoteId?: string;
      codecId?: string;
      active?: boolean;
      retransmittedPacketsSent?: number;
      retransmittedBytesSent?: number;
    };
    const kind = o.kind || o.mediaType;
    if (kind !== 'audio' && kind !== 'video') return;
    const ssrc = Number(o.ssrc || 0);
    const ri = remoteInbound.get(ssrc) as
      | (RTCStats & {
          packetsLost?: number;
          jitter?: number;
          roundTripTime?: number;
          roundTripTimeMeasurements?: number;
          fractionLost?: number;
        })
      | undefined;
    const src = o.mediaSourceId ? mediaSources.get(o.mediaSourceId) : undefined;
    const ms = src as
      | (RTCStats & {
          totalSamplesDuration?: number;
          totalAudioEnergy?: number;
          audioLevel?: number;
          trackIdentifier?: string;
        })
      | undefined;
    streams.push({
      stats_id: o.id,
      raw_timestamp: o.timestamp,
      ssrc,
      kind,
      codecId: o.codecId ?? null,
      mediaSourceId: o.mediaSourceId ?? null,
      remoteId: o.remoteId ?? null,
      packetsSent: o.packetsSent ?? null,
      bytesSent: o.bytesSent ?? null,
      retransmittedPacketsSent: o.retransmittedPacketsSent ?? null,
      retransmittedBytesSent: o.retransmittedBytesSent ?? null,
      nackCount: o.nackCount ?? null,
      active: o.active ?? null,
      remote_inbound: ri
        ? {
            id: ri.id,
            timestamp: ri.timestamp,
            packetsLost: ri.packetsLost ?? null,
            jitter: ri.jitter ?? null,
            rtt_s: ri.roundTripTime ?? null,
            rtt_measurements: ri.roundTripTimeMeasurements ?? null,
            fractionLost: ri.fractionLost ?? null,
          }
        : null,
      media_source: ms
        ? {
            id: ms.id,
            totalSamplesDuration: ms.totalSamplesDuration ?? null,
            totalAudioEnergy: ms.totalAudioEnergy ?? null,
            audioLevel: ms.audioLevel ?? null,
            trackIdentifier: ms.trackIdentifier ?? null,
          }
        : null,
      unavailable: {
        ...(ri ? {} : { remote_inbound: 'no_matched_remote_inbound_rtp' }),
        ...(ms ? {} : { media_source: 'no_media_source_link' }),
        packetsLost_if_null: ri && ri.packetsLost == null ? 'field_absent_not_zero' : undefined,
      },
    });
  });
  out.streams = streams;
  return out;
}

export function installPulseDiag(
  room: Room,
  sessionId: string,
  sessionMeta?: Record<string, unknown> | null,
): { dispose: () => void; collector: BrowserDiagCollector; emitEndFlow: (data: Record<string, unknown>) => void } {
  const activation = resolveBrowserActivation(sessionMeta);
  const collector = new BrowserDiagCollector({
    activation,
    sessionId,
    roomName: room.name || null,
  });
  collector.emit(
    'diag.runtime.provenance',
    {
      component: 'browser',
      livekit_js: (room as unknown as { engine?: { clientInfo?: unknown } }).engine ? 'present' : 'unknown',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      input_kind: activation.input_kind,
      time_origin: typeof performance !== 'undefined' ? performance.timeOrigin : null,
      bundle_digest: null,
      unavailable: {
        bundle_digest: 'not_injected_at_build',
        livekit_js_semver: 'not_read_from_package',
      },
    },
    { force: true, provenance: 'reported' },
  );
  collector.emit('diag.runtime.activation', activation as unknown as Record<string, unknown>, {
    force: true,
  });

  const cleanups: Array<() => void> = [];
  const intervals: number[] = [];

  const flush = () => collector.dispatch(room, sessionId);

  // Source / track state (existing events only — no graph mutation).
  const onMute = (pub: TrackPublication, participant?: Participant) => {
    if (participant && !participant.isLocal) return;
    collector.emit('diag.client.track_state', {
      kind: String(pub.kind),
      source: String(pub.source),
      track_sid: pub.trackSid,
      state: 'muted',
      enabled: pub.track?.mediaStreamTrack?.enabled ?? null,
      readyState: pub.track?.mediaStreamTrack?.readyState ?? null,
      trigger: 'RoomEvent.TrackMuted',
    });
  };
  const onUnmute = (pub: TrackPublication, participant?: Participant) => {
    if (participant && !participant.isLocal) return;
    collector.emit('diag.client.track_state', {
      kind: String(pub.kind),
      source: String(pub.source),
      track_sid: pub.trackSid,
      state: 'unmuted',
      enabled: pub.track?.mediaStreamTrack?.enabled ?? null,
      readyState: pub.track?.mediaStreamTrack?.readyState ?? null,
      trigger: 'RoomEvent.TrackUnmuted',
    });
  };
  room.on(RoomEvent.TrackMuted, onMute as (...args: unknown[]) => void);
  room.on(RoomEvent.TrackUnmuted, onUnmute as (...args: unknown[]) => void);
  cleanups.push(() => {
    room.off(RoomEvent.TrackMuted, onMute as (...args: unknown[]) => void);
    room.off(RoomEvent.TrackUnmuted, onUnmute as (...args: unknown[]) => void);
  });

  room.localParticipant.getTrackPublications().forEach((pub) => {
    const lp = pub as LocalTrackPublication;
    if (lp.kind === Track.Kind.Audio || lp.source === Track.Source.Microphone) {
      collector.emit('diag.client.source_registered', {
        track_sid: lp.trackSid,
        source: String(lp.source),
        kind: String(lp.kind),
        muted: lp.isMuted,
        generation: 1,
      });
    }
  });

  collector.emit('diag.client.context_state', {
    role: 'playback_capability',
    can_playback_audio: room.canPlaybackAudio,
    note: 'not_capture_health',
  });

  const statsIntervalMs = activation.mode === 'trace' ? 1000 : activation.mode === 'summary' ? 2000 : 0;
  if (statsIntervalMs > 0) {
    const sampleStart = performance.now();
    intervals.push(
      window.setInterval(() => {
        const started = performance.now();
        void collectPerStreamStats(room)
          .then((stats) => {
            collector.emit('diag.client.stats_sample', {
              ...stats,
              sampling_start_ms: started - sampleStart,
              collection_duration_ms: performance.now() - started,
              actual_interval_ms: statsIntervalMs,
            });
            flush();
          })
          .catch((err) => {
            collector.emit('diag.client.stats_failed', {
              failure_class: err?.name || 'Error',
            });
          });
      }, statsIntervalMs),
    );
  }

  const emitEndFlow = (data: Record<string, unknown>) => {
    collector.emit('diag.client.end_flow', data, { force: true });
    flush();
  };

  return {
    collector,
    emitEndFlow,
    dispose: () => {
      intervals.forEach((id) => window.clearInterval(id));
      cleanups.forEach((fn) => fn());
      collector.emit('diag.collector.final', collector.health() as unknown as Record<string, unknown>, {
        force: true,
      });
      flush();
    },
  };
}
